import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import type {
	AI_SUGGESTION_TRIGGERED_FROM,
	AIInlineSuggestionPayload,
} from '@atlaskit/editor-common/analytics/types/ai-inline-suggestion-events';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { Command } from '@atlaskit/editor-common/types/command';
import { withAnalytics } from '@atlaskit/editor-common/withAnalytics';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import { getPluginState as getInlineSuggestionPluginState } from '../plugin-factory';
import { insertInlineSuggestion, clearSuggestion } from './commands';

const getInlineSuggestionDetails = (state: EditorState) => {
	const pluginState = getInlineSuggestionPluginState(state);
	const { inlineSuggestion, locale, inlineSuggestionSessionId } = pluginState;
	return {
		matchedFormat:
			inlineSuggestion?.type === 'date' ? inlineSuggestion?.metadata.matchedFormat : undefined,
		suggestionType: inlineSuggestion?.type,
		locale,
		inlineSuggestionSessionId,
	};
};

const getInlineSuggestionInsertPayload = ({
	state,
	triggeredFrom,
}: {
	state: EditorState;
	triggeredFrom: AI_SUGGESTION_TRIGGERED_FROM;
}): AIInlineSuggestionPayload => {
	const { matchedFormat, suggestionType, locale, inlineSuggestionSessionId } =
		getInlineSuggestionDetails(state);
	return {
		eventType: EVENT_TYPE.TRACK,
		action: ACTION.SUGGESTION_INSERTED,
		actionSubject: ACTION_SUBJECT.EDITOR_PLUGIN_AI,
		actionSubjectId: ACTION_SUBJECT_ID.AI_INLINE_SUGGESTION,
		attributes: {
			locale,
			suggestionType,
			triggeredFrom,
			matchedFormat,
			inlineSuggestionSessionId,
		},
	};
};

export const insertInlineSuggestionWithAnalytics =
	(analyticsApi: EditorAnalyticsAPI | undefined) =>
	(triggeredFrom: AI_SUGGESTION_TRIGGERED_FROM): Command => {
		/* This is a "curry" function to fire 2 events
		 * The reason we fire 2 so that we can track the suggestion inserted event
		 * and also the document event (wider editor analytics) for the date suggestion
		 */
		const insertInlineSuggestionWithAnalyticsCommand = withAnalytics(
			analyticsApi,
			(state: EditorState): AIInlineSuggestionPayload => {
				return getInlineSuggestionInsertPayload({ state, triggeredFrom });
			},
		)(insertInlineSuggestion());

		return withAnalytics(analyticsApi, (): AIInlineSuggestionPayload => {
			return {
				action: ACTION.INSERTED,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.DATE,
				eventType: EVENT_TYPE.TRACK,
				attributes: { inputMethod: INPUT_METHOD.INLINE_SUGGESTION_FLOATING_TB },
			};
		})(insertInlineSuggestionWithAnalyticsCommand);
	};

export const clearSuggestionWithAnalytics =
	(analyticsApi: EditorAnalyticsAPI | undefined) =>
	(triggeredFrom: AI_SUGGESTION_TRIGGERED_FROM): Command =>
		withAnalytics(analyticsApi, (state: EditorState): AIInlineSuggestionPayload => {
			const { matchedFormat, suggestionType, locale, inlineSuggestionSessionId } =
				getInlineSuggestionDetails(state);
			return {
				eventType: EVENT_TYPE.TRACK,
				action: ACTION.SUGGESTION_DISMISSED,
				actionSubject: ACTION_SUBJECT.EDITOR_PLUGIN_AI,
				actionSubjectId: ACTION_SUBJECT_ID.AI_INLINE_SUGGESTION,
				attributes: {
					locale,
					suggestionType,
					triggeredFrom,
					matchedFormat,
					inlineSuggestionSessionId,
				},
			};
		})(clearSuggestion());

const getNewSuggestionAnalytics = () => {
	return ({
		analyticsApi,
		state,
	}: {
		analyticsApi: EditorAnalyticsAPI | undefined;
		state: EditorState;
	}): void => {
		const { matchedFormat, suggestionType, locale, inlineSuggestionSessionId } =
			getInlineSuggestionDetails(state);

		analyticsApi?.fireAnalyticsEvent({
			eventType: EVENT_TYPE.TRACK,
			action: ACTION.SUGGESTION_VIEWED,
			actionSubject: ACTION_SUBJECT.EDITOR_PLUGIN_AI,
			actionSubjectId: ACTION_SUBJECT_ID.AI_INLINE_SUGGESTION,
			attributes: {
				matchedFormat,
				suggestionType,
				locale,
				inlineSuggestionSessionId,
			},
		});
	};
};

export const fireNewSuggestionAnalytics: ({
	analyticsApi,
	state,
}: {
	analyticsApi: EditorAnalyticsAPI | undefined;
	state: EditorState;
}) => void = getNewSuggestionAnalytics();
